import { useState } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import type { FunnelStep } from '../../api/client';

interface FunnelBuilderProps {
  onClose: () => void;
  onSave: (data: { name: string; steps: FunnelStep[] }) => void;
  isSaving: boolean;
}

export default function FunnelBuilder({ onClose, onSave, isSaving }: FunnelBuilderProps) {
  const [name, setName] = useState('');
  const [steps, setSteps] = useState<FunnelStep[]>([
    { name: 'Step 1', type: 'pageview', value: '' },
    { name: 'Step 2', type: 'pageview', value: '' },
  ]);

  function addStep() {
    setSteps([...steps, { name: `Step ${steps.length + 1}`, type: 'pageview', value: '' }]);
  }

  function removeStep(index: number) {
    if (steps.length <= 2) return;
    setSteps(steps.filter((_, i) => i !== index));
  }

  function updateStep(index: number, updates: Partial<FunnelStep>) {
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || steps.some((s) => !s.value.trim())) return;
    onSave({ name, steps });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Create Funnel</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Funnel Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input mt-1"
              placeholder="e.g. Checkout Flow"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Steps</label>
            <div className="space-y-2">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 p-3"
                >
                  <GripVertical className="h-4 w-4 shrink-0 text-gray-300" />
                  <input
                    type="text"
                    value={step.name}
                    onChange={(e) => updateStep(i, { name: e.target.value })}
                    className="w-28 rounded border-gray-300 text-sm"
                    placeholder="Step name"
                  />
                  <select
                    value={step.type}
                    onChange={(e) =>
                      updateStep(i, { type: e.target.value as 'pageview' | 'event' })
                    }
                    className="rounded border-gray-300 text-sm"
                  >
                    <option value="pageview">Page View</option>
                    <option value="event">Event</option>
                  </select>
                  <input
                    type="text"
                    value={step.value}
                    onChange={(e) => updateStep(i, { value: e.target.value })}
                    className="flex-1 rounded border-gray-300 text-sm"
                    placeholder={step.type === 'pageview' ? '/page-url' : 'event_name'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeStep(i)}
                    disabled={steps.length <= 2}
                    className="text-gray-400 hover:text-red-500 disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addStep}
              className="mt-2 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
            >
              <Plus className="h-4 w-4" />
              Add Step
            </button>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSaving} className="btn-primary">
              {isSaving ? 'Creating...' : 'Create Funnel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
